import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box, Chip, CircularProgress, Fab, IconButton, Paper, Stack,
    TextField, Tooltip, Typography, Zoom,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WifiOffIcon from '@mui/icons-material/WifiOff';

import { assist } from '../../services/assist';
import { conectarAssist } from '../../services/websocket';
import { EVENTO_FUNCIONALIDADES, funcionalidadeAtiva } from '../../services/configuracao';
import { alertaFalha, toastAviso, toastSucesso } from '../../utils/toast';
import { confirmar } from '../../utils/confirmacao';

const LARGURA_PAINEL = 360;
const ALTURA_PAINEL = 540;

// Atalhos sugeridos na conversa vazia (issue #421, seção 2). Só preenchem e
// enviam a pergunta — não são um contrato de dado com o back.
const ATALHOS_INICIAIS = ['Carga', 'Descarga', 'Romaneio', 'Fila', 'Cadastro', 'Painel', 'Outro problema'];

const STATUS_FINAIS = ['RESOLVIDA', 'ENCERRADA'];
const STATUS_HUMANO = ['AGUARDANDO_HUMANO', 'ATENDIMENTO_HUMANO'];

// Alguns provedores (o compat da Gemini, por exemplo) mandam a resposta em
// dois ou três pedaços grandes em vez de token a token — tecnicamente ainda é
// streaming, mas na tela o texto salta pronto de uma vez, exatamente o efeito
// "resposta de robô" que o streaming existia para evitar. Por isso a
// revelação é sempre pautada no cliente, character a character, na
// velocidade de alguém digitando rápido — nunca na velocidade de chegada do
// pedaço pela rede.
const PASSO_REVELACAO_MS = 25;
const CARACTERES_POR_PASSO = 1;

const formatarHora = (valor) => (valor
    ? new Date(valor).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : '');

// Uma "fala" vira várias caixas — um parágrafo por caixa — porque é assim que
// uma pessoa manda mensagem de verdade: textos curtos, um atrás do outro, não
// um bloco só com quebra de linha no meio. O prompt (MontadorPromptAssist) já
// pede frase curta em vez de parágrafo de wiki, então normalmente é só uma ou
// duas caixas.
const paragrafosDe = (texto) => texto.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

// Reticências pulsando, em vez de "…" parado — CSS puro, sem timer/estado.
const PontosAnimados = () => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            ml: 0.2,
            '& > span': { animation: 'pontosAssistPulsar 1.4s infinite ease-in-out both' },
            '& > span:nth-of-type(2)': { animationDelay: '0.2s' },
            '& > span:nth-of-type(3)': { animationDelay: '0.4s' },
            '@keyframes pontosAssistPulsar': {
                '0%, 80%, 100%': { opacity: 0.2 },
                '40%': { opacity: 1 },
            },
        }}
    >
        <span>.</span><span>.</span><span>.</span>
    </Box>
);

// Uma fala da conversa. Mensagem de SISTEMA não é bolha — não é fala de
// ninguém (AssistMapper/RemetenteAssist, no back), só um aviso central.
const Bolha = ({ mensagem, feedback, onFeedback }) => {
    const deUsuario = mensagem.remetente === 'USUARIO';
    const doSistema = mensagem.remetente === 'SISTEMA';
    const daIa = mensagem.remetente === 'ASSISTENTE';

    if (doSistema) {
        return (
            <Typography align="center" color="text.secondary" variant="caption" sx={{ display: 'block', my: 1 }}>
                {mensagem.conteudo}
            </Typography>
        );
    }

    const paragrafos = paragrafosDe(mensagem.conteudo);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: deUsuario ? 'flex-end' : 'flex-start', mb: 1.5 }}>
            {/* Quem respondeu: nome do atendente humano, ou "MPG Assist" para a
                IA — sem isso as duas falas eram visualmente idênticas, e não
                dava para saber se quem respondeu já tinha sido um atendente. */}
            {(mensagem.autorNome || daIa) && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.3 }}>
                    {mensagem.autorNome ? `${mensagem.autorNome} — Suporte` : 'MPG Assist'}
                </Typography>
            )}
            {paragrafos.map((paragrafo, indice) => (
                <Paper
                    // eslint-disable-next-line react/no-array-index-key
                    key={indice}
                    elevation={0}
                    sx={{
                        px: 1.5, py: 1, maxWidth: '85%', borderRadius: 2,
                        bgcolor: deUsuario ? 'primary.main' : 'background.default',
                        color: deUsuario ? 'primary.contrastText' : 'text.primary',
                        mt: indice > 0 ? 0.5 : 0,
                    }}
                >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{paragrafo}</Typography>
                </Paper>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3 }}>
                {formatarHora(mensagem.criadoEm)}
            </Typography>
            {daIa && onFeedback && (
                feedback ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3 }}>
                        {feedback === 'up' ? 'Obrigado pelo retorno!' : 'Anotado — chamando o suporte.'}
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.3 }}>
                        <Tooltip title="Resolveu">
                            <IconButton size="small" aria-label="Resolveu" onClick={() => onFeedback(mensagem.id, 'up')}>
                                <ThumbUpAltOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Não resolveu">
                            <IconButton size="small" aria-label="Não resolveu" onClick={() => onFeedback(mensagem.id, 'down')}>
                                <ThumbDownAltOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )
            )}
        </Box>
    );
};

// Botão flutuante + janela do MPG Assist, montado uma vez no MainLayout para
// aparecer em toda tela autenticada. Fica de fora quando a funcionalidade
// ASSIST está desligada na instalação.
const AssistWidget = () => {
    const location = useLocation();
    const [disponivel, setDisponivel] = useState(() => funcionalidadeAtiva('ASSIST'));
    const [aberto, setAberto] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [processando, setProcessando] = useState(false);
    const [streamTexto, setStreamTexto] = useState('');
    const [conectado, setConectado] = useState(true);
    const [conversa, setConversa] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [texto, setTexto] = useState('');
    const [feedbacks, setFeedbacks] = useState({});

    const desconectarRef = useRef(null);
    const listaRef = useRef(null);
    // Fila de texto ainda não revelado na tela e a mensagem final que chegou
    // enquanto ela ainda estava sendo revelada — a rede entrega o pedaço
    // inteiro na hora, mas a revelação segue no próprio ritmo (ver
    // PASSO_REVELACAO_MS) até a fila esvaziar, só então troca pela mensagem
    // definitiva. Sem isso, mensagem grande cortaria o "efeito digitando" na
    // metade.
    const filaRevelacaoRef = useRef('');
    const intervaloRevelacaoRef = useRef(null);
    const mensagemPendenteRef = useRef(null);

    const pararRevelacao = useCallback(() => {
        if (intervaloRevelacaoRef.current) {
            clearInterval(intervaloRevelacaoRef.current);
            intervaloRevelacaoRef.current = null;
        }
        filaRevelacaoRef.current = '';
        mensagemPendenteRef.current = null;
    }, []);

    useEffect(() => () => pararRevelacao(), [pararRevelacao]);

    useEffect(() => {
        const aoMudar = () => setDisponivel(funcionalidadeAtiva('ASSIST'));
        window.addEventListener(EVENTO_FUNCIONALIDADES, aoMudar);
        return () => window.removeEventListener(EVENTO_FUNCIONALIDADES, aoMudar);
    }, []);

    const statusAtual = conversa?.status;
    const encerrada = STATUS_FINAIS.includes(statusAtual);
    const comAtendente = statusAtual === 'ATENDIMENTO_HUMANO';
    const podePedirAtendente = conversa && !encerrada && !STATUS_HUMANO.includes(statusAtual);

    const rolarParaFim = useCallback(() => {
        requestAnimationFrame(() => {
            if (listaRef.current) listaRef.current.scrollTop = listaRef.current.scrollHeight;
        });
    }, []);

    // Idempotente por id: a mesma mensagem chega duas vezes quando é a
    // própria (resposta do POST + eco pelo WebSocket).
    const adicionarMensagem = useCallback((mensagem) => {
        setMensagens((atual) => (atual.some((m) => m.id === mensagem.id) ? atual : [...atual, mensagem]));
        rolarParaFim();
    }, [rolarParaFim]);

    // Puxa um pedacinho da fila para a tela a cada passo, no ritmo de alguém
    // digitando — não no ritmo em que o pedaço chegou pela rede. Ao esvaziar,
    // se já tinha mensagem final esperando (chegou no meio da revelação),
    // troca o balão "digitando" pela mensagem de verdade.
    const iniciarRevelacao = useCallback(() => {
        if (intervaloRevelacaoRef.current) return;
        intervaloRevelacaoRef.current = setInterval(() => {
            const restante = filaRevelacaoRef.current;
            if (!restante) {
                clearInterval(intervaloRevelacaoRef.current);
                intervaloRevelacaoRef.current = null;
                const pendente = mensagemPendenteRef.current;
                if (pendente) {
                    mensagemPendenteRef.current = null;
                    setStreamTexto('');
                    adicionarMensagem(pendente.mensagem);
                    if (pendente.status) {
                        setConversa((atual) => (atual ? { ...atual, status: pendente.status } : atual));
                    }
                }
                return;
            }
            const pedaco = restante.slice(0, CARACTERES_POR_PASSO);
            filaRevelacaoRef.current = restante.slice(pedaco.length);
            setStreamTexto((atual) => atual + pedaco);
            rolarParaFim();
        }, PASSO_REVELACAO_MS);
    }, [adicionarMensagem, rolarParaFim]);

    const aoReceberEvento = useCallback((evento) => {
        switch (evento.tipo) {
            case 'PROCESSANDO':
                pararRevelacao();
                setProcessando(true);
                setStreamTexto('');
                break;
            // Pedaço da resposta da IA, ainda sendo gerada — entra na fila e
            // sai de lá no ritmo humano (ver iniciarRevelacao), não assim que
            // chega — provedor pode mandar tudo de uma vez.
            case 'TRECHO':
                setProcessando(false);
                filaRevelacaoRef.current += evento.trecho;
                iniciarRevelacao();
                break;
            case 'MENSAGEM':
                setProcessando(false);
                // Ainda revelando trecho pendente: espera esvaziar a fila
                // antes de trocar pelo balão definitivo.
                if (intervaloRevelacaoRef.current || filaRevelacaoRef.current) {
                    mensagemPendenteRef.current = { mensagem: evento.mensagem, status: evento.status };
                } else {
                    setStreamTexto('');
                    adicionarMensagem(evento.mensagem);
                    if (evento.status) setConversa((atual) => (atual ? { ...atual, status: evento.status } : atual));
                }
                break;
            case 'STATUS_ALTERADO':
                setProcessando(false);
                setConversa((atual) => (atual ? { ...atual, status: evento.status } : atual));
                break;
            case 'ATENDENTE_ENTROU':
                setConversa((atual) => (atual
                    ? { ...atual, status: evento.status, atendenteNome: evento.atendenteNome }
                    : atual));
                break;
            case 'ERRO':
                pararRevelacao();
                setProcessando(false);
                setStreamTexto('');
                if (evento.status) setConversa((atual) => (atual ? { ...atual, status: evento.status } : atual));
                break;
            default:
                break;
        }
    }, [adicionarMensagem, pararRevelacao, iniciarRevelacao]);

    // Só uma pista para o atendente ver onde a pessoa está — o back nunca
    // confia nisso sozinho (ResolvedorContextoAssist, fase 5).
    const tela = useMemo(() => location.pathname.replace(/^\//, '').slice(0, 60) || 'INICIO', [location.pathname]);

    const abrirConversa = useCallback(async () => {
        setCarregando(true);
        try {
            const resposta = await assist.abrir({ origem: 'WEB', tela });
            setConversa(resposta);
            setMensagens(resposta.mensagens || []);
            desconectarRef.current = conectarAssist(resposta.id, aoReceberEvento, setConectado);
            rolarParaFim();
        } catch (e) {
            alertaFalha(e, 'Não foi possível abrir o suporte agora.');
            setAberto(false);
        } finally {
            setCarregando(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tela, aoReceberEvento]);

    const alternarPainel = useCallback(() => {
        setAberto((atual) => {
            const novo = !atual;
            if (novo && !conversa) abrirConversa();
            return novo;
        });
    }, [conversa, abrirConversa]);

    useEffect(() => () => { desconectarRef.current?.(); }, []);

    const enviar = useCallback(async (conteudo) => {
        const mensagem = (conteudo ?? texto).trim();
        if (!mensagem || !conversa || enviando) return;
        setEnviando(true);
        try {
            const salva = await assist.enviarMensagem(conversa.id, mensagem);
            adicionarMensagem(salva);
            setTexto('');
            if (statusAtual === 'ATENDIMENTO_IA') setProcessando(true);
        } catch (e) {
            alertaFalha(e);
        } finally {
            setEnviando(false);
        }
    }, [texto, conversa, enviando, statusAtual, adicionarMensagem]);

    const pedirAtendente = useCallback(async () => {
        if (!conversa) return;
        try {
            const atualizada = await assist.solicitarAtendente(conversa.id);
            setConversa((atual) => ({ ...atual, status: atualizada.status }));
            toastSucesso('Encaminhado para atendimento humano. Alguém já vai responder.');
        } catch (e) {
            alertaFalha(e);
        }
    }, [conversa]);

    // Saída de quem a IA não resolveu e ninguém assumiu ainda — o número é
    // configuração da instalação (SupportHandoffService, fase 8); sem ele o
    // botão simplesmente não tem para onde mandar.
    const continuarNoWhatsApp = useCallback(async () => {
        if (!conversa) return;
        try {
            const { url } = await assist.whatsapp(conversa.id);
            if (!url) {
                toastAviso('WhatsApp não configurado nesta instalação.');
                return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            alertaFalha(e);
        }
    }, [conversa]);

    const encerrarConversa = useCallback(async () => {
        if (!conversa) return;
        if (!await confirmar({ titulo: 'Encerrar atendimento?', textoConfirmar: 'Encerrar', icone: 'question' })) return;
        try {
            const atualizada = await assist.encerrar(conversa.id);
            setConversa((atual) => ({ ...atual, status: atualizada.status }));
        } catch (e) {
            alertaFalha(e);
        }
    }, [conversa]);

    const novoAtendimento = useCallback(() => {
        desconectarRef.current?.();
        pararRevelacao();
        setConversa(null);
        setMensagens([]);
        setFeedbacks({});
        setStreamTexto('');
        abrirConversa();
    }, [abrirConversa, pararRevelacao]);

    // 👍 confirma que a resposta resolveu — mesmo desfecho de quem clica em
    // "encerrar" satisfeito (AssistConversaService#resolver). 👎 é o
    // escalonamento: aciona o mesmo caminho do botão "falar com atendente".
    const aoDarFeedback = useCallback(async (mensagemId, valor) => {
        setFeedbacks((atual) => ({ ...atual, [mensagemId]: valor }));
        if (valor === 'down') {
            await pedirAtendente();
            return;
        }
        try {
            const atualizada = await assist.resolver(conversa.id);
            setConversa((atual) => ({ ...atual, status: atualizada.status }));
        } catch (e) {
            alertaFalha(e);
        }
    }, [conversa, pedirAtendente]);

    if (!disponivel) return null;

    return (
        <>
            <Zoom in={!aberto} unmountOnExit>
                <Fab
                    color="primary"
                    onClick={alternarPainel}
                    sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1300 }}
                    aria-label="Abrir o MPG Assist"
                >
                    <ChatIcon />
                </Fab>
            </Zoom>

            <Zoom in={aberto} unmountOnExit>
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed', right: 24, bottom: 24, zIndex: 1300,
                        width: LARGURA_PAINEL, maxWidth: 'calc(100vw - 32px)',
                        height: ALTURA_PAINEL, maxHeight: 'calc(100vh - 100px)',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    }}
                >
                    <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
                        <SupportAgentIcon sx={{ mr: 1 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>MPG Assist</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                {comAtendente && conversa?.atendenteNome
                                    ? `${conversa.atendenteNome} — Suporte`
                                    : encerrada ? 'Atendimento encerrado' : 'Assistente automático'}
                            </Typography>
                        </Box>
                        {!conectado && (
                            <Box role="status" aria-label="Reconectando" sx={{ display: 'flex', mr: 1 }}>
                                <Tooltip title="Reconectando...">
                                    <WifiOffIcon fontSize="small" sx={{ opacity: 0.85 }} />
                                </Tooltip>
                            </Box>
                        )}
                        <IconButton size="small" onClick={() => setAberto(false)} sx={{ color: 'inherit' }} aria-label="Fechar">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box ref={listaRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, bgcolor: 'background.paper' }}>
                        {carregando && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {mensagens.map((mensagem) => (
                            <Bolha
                                key={mensagem.id}
                                mensagem={mensagem}
                                feedback={feedbacks[mensagem.id]}
                                onFeedback={encerrada ? undefined : aoDarFeedback}
                            />
                        ))}

                        {/* Atalhos logo depois da mensagem de boas-vindas do assistente
                            (AssistConversaService#criar) — somem assim que a pessoa manda
                            a primeira fala, porque deixam de fazer sentido depois disso. */}
                        {!carregando && mensagens.every((m) => m.remetente !== 'USUARIO') && (
                            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
                                {ATALHOS_INICIAIS.map((atalho) => (
                                    <Chip key={atalho} label={atalho} size="small" onClick={() => enviar(atalho)} />
                                ))}
                            </Stack>
                        )}

                        {/* Pedaços da resposta chegando pelo WebSocket, ainda sem id —
                            a bolha "de verdade" (com id, botões de feedback) só nasce
                            quando o evento MENSAGEM final chega e some esta. */}
                        {streamTexto && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.3 }}>
                                    MPG Assist
                                </Typography>
                                {paragrafosDe(streamTexto).map((paragrafo, indice) => (
                                    <Paper
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={indice}
                                        elevation={0}
                                        sx={{
                                            px: 1.5, py: 1, maxWidth: '85%', borderRadius: 2,
                                            bgcolor: 'background.default', color: 'text.primary',
                                            mt: indice > 0 ? 0.5 : 0,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{paragrafo}</Typography>
                                    </Paper>
                                ))}
                            </Box>
                        )}

                        {processando && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 0.5 }}>
                                <CircularProgress size={14} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    aria-label="Analisando"
                                    sx={{ display: 'flex', alignItems: 'baseline' }}
                                >
                                    Analisando<PontosAnimados />
                                </Typography>
                            </Stack>
                        )}
                    </Box>

                    {conversa && !encerrada && (
                        <Box sx={{ px: 1.5, pt: 1 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {podePedirAtendente && (
                                    <Chip
                                        size="small"
                                        icon={<SupportAgentIcon fontSize="small" />}
                                        label="Falar com atendente"
                                        onClick={pedirAtendente}
                                    />
                                )}
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={<WhatsAppIcon fontSize="small" />}
                                    label="Continuar pelo WhatsApp"
                                    onClick={continuarNoWhatsApp}
                                />
                            </Stack>
                        </Box>
                    )}

                    <Box sx={{ p: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        {encerrada ? (
                            <Chip label="Iniciar novo atendimento" color="primary" onClick={novoAtendimento} />
                        ) : (
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Digite sua dúvida..."
                                    value={texto}
                                    disabled={carregando || !conversa}
                                    onChange={(e) => setTexto(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
                                    }}
                                    inputProps={{ maxLength: 2000 }}
                                />
                                <IconButton
                                    color="primary"
                                    onClick={() => enviar()}
                                    disabled={enviando || !texto.trim() || carregando || !conversa}
                                    aria-label="Enviar"
                                >
                                    <SendIcon />
                                </IconButton>
                            </Stack>
                        )}
                        {conversa && !encerrada && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                onClick={encerrarConversa}
                                sx={{ display: 'block', textAlign: 'right', mt: 0.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Encerrar atendimento
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Zoom>
        </>
    );
};

export default AssistWidget;
